import { Injectable, inject } from '@angular/core';
import { NAVIGATOR } from '@ng-web-apis/common';
import { logger } from '@app/shared';

export interface DeviceInfo {
  deviceType: 'phone' | 'tablet' | 'desktop';
  platform: 'ios' | 'android' | 'generic';
}

/**
 * Recognises the device from the user agent: the first matching rule wins, in
 * the order generic (Windows phone and tablet, Mac), iOS, Android; anything
 * else is a generic desktop. An iPad with iPadOS 13 or later reports a Mac and
 * therefore counts as desktop, as it did before.
 */
export const detectDevice = (userAgent: string): DeviceInfo => {
  const isWindowsPhone =
    /windows phone/i.test(userAgent) || /WPDesktop/.test(userAgent);
  const isWindowsTablet =
    !isWindowsPhone && /Windows(.*)arm(.*)Tablet PC/i.test(userAgent);
  const isAppHost =
    !isWindowsPhone && !isWindowsTablet && /msapphost/i.test(userAgent);
  const isMac = /((intel|ppc) mac os x)/.test(userAgent.toLowerCase());
  if (isWindowsPhone || isWindowsTablet || isAppHost || isMac) {
    return {
      deviceType: isWindowsPhone
        ? 'phone'
        : isWindowsTablet
          ? 'tablet'
          : 'desktop',
      platform: 'generic',
    };
  }
  if (/ip(hone|od|ad)/i.test(userAgent)) {
    return {
      deviceType: /ip(hone|od)/i.test(userAgent) ? 'phone' : 'tablet',
      platform: 'ios',
    };
  }
  if (/android|htc_|silk/i.test(userAgent)) {
    return {
      deviceType: /mobile/i.test(userAgent) ? 'phone' : 'tablet',
      platform: 'android',
    };
  }
  return { deviceType: 'desktop', platform: 'generic' };
};

@Injectable({ providedIn: 'root' })
export class DeviceService {
  private readonly device: DeviceInfo;

  constructor() {
    const navigator = inject<Navigator>(NAVIGATOR);

    this.device = detectDevice(navigator.userAgent || '');
    logger.log(this.device);
  }

  get isPhone() {
    return this.device.deviceType === 'phone';
  }

  get isTablet() {
    return this.device.deviceType === 'tablet';
  }

  get isMobile() {
    return this.isPhone || this.isTablet;
  }

  get isIOS() {
    return this.device.platform === 'ios';
  }

  get isAndroid() {
    return this.device.platform === 'android';
  }
}
